import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import { Button, Pagination, Loader, GSGrid, View, FormGroup } from "@components";
import type { State } from "@store/reducers";
import type {
  AbsenceAuditLog,
  AuditLogsFilterType,
} from "@modules/service/reducer/audit-logs/types";
import { type PaginationExtendedType } from "@types";
import { route } from "@routes/helper";
import { AuditLogsFilter } from "@modules/shared/components";
import moment from "moment";

type PropsFromState = {
  isLoading: boolean,
  list: Array<AbsenceAuditLog>,
  pagination: PaginationExtendedType,
};

type PropsFromDispatch = {
  getAbsenceAuditLogs: typeof actionCreators.GetAbsenceAuditLogs,
  exportAbsenceAuditLogs: typeof actionCreators.ExportAbsenceAuditLogs,
  getAuditLogsReset: typeof actionCreators.GetAuditLogsReset,
};

type Props = {
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  clientId: number,
};

export function AbsenceAuditLogs(props: Props) {
  const { list, pagination, clientId, t } = props;
  const [searchParams, setSearchParams]: [?AuditLogsFilterType, Function] = useState({
    clientId,
    search: {
      dateFrom: null,
      dateTo: null,
      userId: null,
      employeeId: null,
    },
    page: 1,
  });

  useEffect(() => {
    props.getAuditLogsReset();
  }, []);

  const columns = [
    {
      name: t("USER_ID"),
      key: "revisionId",
      align: "left",
      width: "fit-content",
      renderCell: ({ row }: Object) => row.userId,
    },
    {
      name: t("USER"),
      align: "left",
      key: "userFullName",
    },
    {
      name: t("ACTIVITY_DATE"),
      key: "revisionDate",
      width: "fit-content",
      renderCell: ({ row }: Object) => GSGrid.render.date(row.revisionDate),
    },
    {
      name: t("EMPLOYEE_ID"),
      key: "employeeId",
      align: "left",
      width: "fit-content",
    },
    {
      name: t("EMPLOYEE_RECORD"),
      key: "employeeFullName",
      align: "left",
    },
    {
      name: t("ABSENCE_ID"),
      key: "absenceId",
      width: "fit-content",
    },
    {
      name: t("ABSENCE_STATUS"),
      key: "absenceStatus",
      width: "fit-content",
      renderCell: ({ row }: Object) => GSGrid.render.statusPill(props.t(row.absenceStatus)),
    },
    {
      name: t("VIEW"),
      key: props.t("VIEW"),
      width: 40,
      align: "center",
      onCellClick: (row: Object) =>
        window.open(
          route("REPORTING_EMPLOYEE_ABSENCE_DETAILS_REVISION_PATH", {
            clientId,
            employeeId: row.employeeId,
            absenceId: row.absenceId,
            revisionId: row.revisionId,
          }),
          "_blank",
        ),
      renderCell: ({ row }: Object) => GSGrid.render.materialIcon("play_circle"),
    },
  ];

  function handleSubmitFilter() {
    props.getAbsenceAuditLogs({ ...searchParams, page: 1 });
  }

  function handleFilterChange(data) {
    setSearchParams({
      ...searchParams,
      search: {
        ...data, // TODO - this ugly custom fix, remove it when the BE respect time zones +02:00
        dateFrom: moment(data.dateFrom)
          .set("hour", 0)
          .set("minute", 0)
          .set("second", 0)
          .utc()
          .toISOString(),
        dateTo: moment(data.dateTo)
          .set("hour", 23)
          .set("minute", 59)
          .set("second", 59)
          .utc()
          .toISOString(),
      },
    });
  }

  function handleExport(e: Event) {
    e.preventDefault();
    props.exportAbsenceAuditLogs(searchParams);
  }

  function handleChangePage(page: string) {
    const res = {
      ...searchParams,
      page: parseInt(page),
    };

    setSearchParams(res);
    props.getAbsenceAuditLogs(res);
  }

  return (
    <View>
      {props.isLoading && <Loader />}
      <View className="message-box" margin={[0, 0, "2rem", 0]}>
        {props.t("ABSENCE_AUDITS_INFORMATION")}
      </View>
      <View gap={4} className="gs-container">
        <View justifyContent="space-between" direction="row" margin={[0, 0, "1rem", 0]}>
          <View direction="row" gap={4} justifyContent="center" alignItems="flex-end">
            <AuditLogsFilter
              handleSubmit={handleSubmitFilter}
              onChange={handleFilterChange}
              data={searchParams.search}
            />
          </View>
          <View direction="row" justifyContent="center" alignItems="flex-end">
            <FormGroup>
              <Button materialIcon="file_download" onClick={handleExport} disabled={!list.length}>
                {t("EXPORT")}
              </Button>
            </FormGroup>
          </View>
        </View>

        <GSGrid columns={columns} rows={list} />

        {pagination && (
          <Pagination
            className="my-3 d-flex flex-fill justify-content-center"
            currentPage={pagination.pageNumber}
            totalPages={pagination.totalPages}
            onChange={handleChangePage}
          />
        )}
      </View>
    </View>
  );
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    list: serviceSelectors.selectAbsenceAuditLogsList(state),
    pagination: serviceSelectors.selectAbsenceAuditLogsPagination(state),
    isLoading: serviceSelectors.isLoadingAuditLogs(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getAbsenceAuditLogs: actionCreators.GetAbsenceAuditLogs,
  exportAbsenceAuditLogs: actionCreators.ExportAbsenceAuditLogs,
  getAuditLogsReset: actionCreators.GetAuditLogsReset,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(AbsenceAuditLogs);
